import { useTemplateStore } from "@/stores/templateStore";
import { updateTemplateSchema } from "@/schemas/template/create";
import { useTemplateFormFields } from "@/hooks/template/useTemplateFormFields";
import { useDetailPage } from "@/hooks/useDetailPage";
import { Button } from "@ab-inbev-maz/compass";
import NavigationHeader from "@/components/navigation/NavigationHeader";
import Loading from "@/features/sso-auth/components/Loading";
import { FormFieldsStep, type FormFieldsStepRef } from "@/components/commos/FormStep";
import { DetailView } from "@/components/commos/DetailView";
import ImagePreview from "@/components/commos/ImagePreview";
import DetailPageFileUpload from "@/components/commos/DetailPageFileUpload";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { catalogService } from "@/services/catalogService";


const details = [
  {
    id: 'name',
    label: "Nombre",
  },
  {
    id: 'brand',
    label: "Marca",
  },
  {
    id: 'format',
    label: "Formato",
  },
  {
    id: 'type',
    label: "Tipo",
  },
  {
    id: 'created_at',
    label: "Fecha de creación",
  }
];

const DetailTemplate = () => {
  const { id } = useParams();
  const getTemplate = useTemplateStore(state => state.getTemplate);
  const updateTemplate = useTemplateStore(state => state.updateTemplate);
  const deleteTemplate = useTemplateStore(state => state.deleteTemplate);

  const { fields, setCatalogs } = useTemplateFormFields();
  const { handleDelete, onSubmit, isFetching, refImage } = useDetailPage({
    updateItem: updateTemplate,
    deleteItem: deleteTemplate,
    itemName: 'Template',
    tablePath: '/templates/table',
  });

  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const formRef = useRef<FormFieldsStepRef>(null);

  const [values, setValues] = useState<Record<string, any>>({
    name: '',
    brand: '',
    format: '',
    type: '',
    created_at: '',
  });

  const valueDetails = useMemo(() => {
    return details.map((detail) => ({
      ...detail,
      value: values[detail.id] || '',
    }));
  }, [values]);

  useEffect(() => {
    const fetchTemplate = async () => {
      if (!id) return;

      const { data } = await getTemplate(id);
      if (!data) return;

      Object.entries({
        ...data,
        created_at: DateTime.fromISO(data.created_at).toFormat('dd/MM/yyyy'),
      }).forEach(([key, value]) => {
        setValues((prev) => ({ ...prev, [key]: value || '' }));
      });

      setSourceImage(data.image);
      setIsLoading(false);
    };
    void fetchTemplate();
  }, [id]);

  useEffect(() => {
    const fetchCatalogs = async () => {
      const catalogs = await catalogService.getCatalogs();
      setCatalogs(catalogs);
    };
    void fetchCatalogs();
  }, [setCatalogs]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
          <Loading message="Esto solo va tomar un momento, estamos cargando el Template." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <NavigationHeader
        title={`Template: ${values.name || ''}`}
        description="Aquí podrás revisar toda la información de tu Template"
        actions={[
          <Button
            key="delete-cancel"
            label={isEditing ? "Cancelar" : "Eliminar"}
            variant="secondary"
            rightIcon={isEditing ? "validation/close" : "system/trash"}
            onClick={isEditing ? setIsEditing.bind(null, false) : handleDelete}
            disabled={isFetching}
          />,
          <Button
            key="edit-save"
            label={isEditing ? "Guardar" : "Editar"}
            variant="primary"
            rightIcon={isEditing ? "functionalities/save" : "functionalities/edit-2"}
            onClick={isEditing ? () => formRef.current?.submit() : setIsEditing.bind(null, true)}
            disabled={isFetching}
          />,
        ]}
      />

      <hr className="border-gray-200 container mx-auto mt-2" />

      <div className="container mx-auto px-4 py-8">
        <p className="mb-3 text-sm font-medium text-gray-700">Información del Template</p>
        <div className="grid lg:grid-cols-[1fr_375px] gap-4">
          <div>
            {
              isEditing
                ? (
                  <div>
                    <div className="p-6 rounded-xl border border-gray-200 bg-white">
                      <FormFieldsStep
                        ref={formRef}
                        schema={updateTemplateSchema}
                        defaultValues={values}
                        fields={fields as any}
                        onComplete={onSubmit}
                        isFetching={isFetching}
                        formId="template-general-info"
                        description=""
                        editMode={true}
                      />
                    </div>
                    <DetailPageFileUpload itemType="Template" refImage={refImage} />
                  </div>
                ) : (
                  <DetailView
                    data={valueDetails}
                  />
                )
            }
          </div>

          <ImagePreview
            sourceImage={sourceImage}
            alt="Template preview"
          />
        </div>
      </div>
    </div>
  );
};

export default DetailTemplate;